import { useEffect, useState } from 'react';
import { api, type AutoMatchResult, type DepositMatchResult } from '../lib/api';
import { ErrorBox, dash, money } from '../components/DataTable';

/**
 * 입금 ↔ 세금계산서 연결 — 개선요청 #12 6번. 기획 정본 `plan/accounting.md` 5-G. 서버 `functions/src/deposit.ts`.
 * 입금시트에서 줄마다 「세금계산서No」를 적던 일을 대신한다. 연결 합계가 계산서 금액에 닿으면 발행대장 입금일자가 자동으로 찍힌다.
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** 입금 한 건에 계산서 잇기·풀기. */
export function DepositMatchForm({ bankTxId, onClose, onChanged }: { bankTxId: number; onClose: () => void; onChanged: () => void }) {
  const [data, setData] = useState<DepositMatchResult | null>(null);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.getDepositMatch({ bankTxId, q: q || undefined })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [bankTxId, q, nonce]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); setNonce((n) => n + 1); onChanged(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  const d = data?.deposit;
  const rest = Number(d?.rest ?? 0);
  return (
    <div className="card space-y-3 p-5 text-sm">
      <h2 className="text-lg font-semibold">입금에 계산서 연결</h2>
      {d && (
        <div className="rounded-lg bg-canvas px-3 py-2">
          <b>{d.tx_date}</b> · {d.corp_name} {d.bank} {d.account_no}
          {d.is_driver_acct && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-800" title="기사매출 계좌 — 기사개인오더 계산서를 먼저 추천합니다">기사매출</span>} · 내용 <b>{dash(d.memo)}</b> · 입금 <b>{money(d.amount_in)}</b>
          {Number(d.matched) > 0 && <> · 연결 {money(d.matched)} · <span className={rest > 0 ? 'text-amber-700' : 'text-emerald-700'}>남음 {money(d.rest)}</span></>}
        </div>
      )}
      {error && <ErrorBox message={error} />}
      {data && data.links.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr className="border-b text-left text-ink-soft"><th className="py-1">연결한 계산서</th><th>거래처</th><th className="text-right">연결 금액</th><th className="text-right" title="이 계산서에 아직 입금이 안 이어진 금액 — 다른 입금에서 이어 붙이면 됩니다">계산서 남음</th><th>연결</th><th /></tr></thead>
          <tbody>
            {data.links.map((l) => (
              <tr key={l.match_id} className="border-b">
                <td className="py-1 font-mono">{l.invoice_no}</td><td>{l.cust_name}</td><td className="text-right">{money(l.amount)}</td>
                <td className={`text-right ${Number(l.inv_rest) > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{money(l.inv_rest)}</td>
                <td className="pl-2 text-ink-faint">{l.auto ? '자동' : dash(l.created_by?.split('@')[0])} {l.created_at}</td>
                <td className="text-right"><button className="btn-ghost text-xs" disabled={busy} onClick={() => run(() => api.unmatchDeposit({ matchId: Number(l.match_id) }))}>풀기</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }} className="flex items-center gap-2">
        <input className="field w-72" placeholder="발행번호·거래처·사업자번호로 찾기" value={term} onChange={(e) => setTerm(e.target.value)} />
        <button className="btn-ghost" type="submit">찾기</button>
        <span className="text-xs text-ink-faint">{q ? `「${q}」 결과` : '추천 = 남은 금액이 입금과 같거나 거래처 이름이 비슷한 미입금 계산서'}</span>
      </form>
      <div className="max-h-80 overflow-auto rounded border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b text-left text-ink-soft">
              <th className="px-2 py-1">발행번호</th><th>정산월</th><th>거래처</th><th>신청 기사</th><th>작성일자</th><th className="text-right">남은 금액</th><th>연결 금액</th><th />
            </tr>
          </thead>
          <tbody>
            {data?.candidates.map((c) => {
              const exact = Number(c.remaining) === rest && rest > 0;
              return (
                <tr key={c.invoice_id} className={`border-b ${exact ? 'bg-emerald-50/60' : ''}`}>
                  <td className="whitespace-nowrap px-2 py-1 font-mono">{c.invoice_no}</td><td>{dash(c.drive_mon)}</td>
                  <td>{c.cust_name}{c.is_driver && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-800">기사</span>}{!c.issuer && <span className="ml-1 text-[10px] text-ink-faint">발행 전</span>}{c.paid_date && !c.paid_by_match && <span className="ml-1 rounded bg-sky-50 px-1 text-[10px] text-sky-800" title="계산서에 이 입금일 근처 입금일자가 이미 적혀 있습니다 — 연결해도 입금일자는 그대로입니다">입금일 {c.paid_date.slice(5)} 적힘</span>}</td>
                  {/* #106 기사 계산서의 신청자 = 「기사명 | 차량번호」(driverOrder·driverInvoice). 회사 계산서의 신청자는 접수담당자라 비운다. */}
                  <td className="whitespace-nowrap">{c.is_driver ? dash(c.requester) : ''}</td>
                  <td className="whitespace-nowrap">{dash(c.written_date)}</td>
                  <td className="text-right">{money(c.remaining)}{exact && <span className="ml-1 text-[10px] text-emerald-700">금액 같음</span>}</td>
                  <td><input className="field w-28 py-0.5 text-right text-xs" placeholder="자동" value={amounts[c.invoice_id] ?? ''}
                    onChange={(e) => setAmounts((m) => ({ ...m, [c.invoice_id]: e.target.value }))}
                    title="비우면 입금 남은 금액과 계산서 남은 금액 중 작은 쪽" /></td>
                  <td className="text-right"><button className="btn text-xs" disabled={busy || rest <= 0}
                    onClick={() => run(() => api.matchDeposit({ bankTxId, invoiceId: Number(c.invoice_id), amount: amounts[c.invoice_id] || undefined }))}>연결</button></td>
                </tr>
              );
            })}
            {data && data.candidates.length === 0 && (
              <tr><td colSpan={8} className="px-2 py-3 text-ink-faint">{q ? '찾는 미입금 계산서가 없습니다.' : '추천할 계산서가 없습니다 — 발행번호나 거래처로 찾아 보세요.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink-faint">연결 합계가 계산서 금액(수정 발행 포함)에 닿으면 발행대장 입금일자 = 이 입금일로 찍힙니다. 풀면 다시 비워집니다. 손으로 입금일자를 적은 계산서는 여기 나오지 않습니다.</p>
      <div className="flex justify-end"><button className="btn-primary" onClick={onClose}>닫기</button></div>
    </div>
  );
}

/** 기간·법인의 아직 연결 없는 입금을 금액·이름이 맞는 계산서 한 장과 한 번에 잇는다(미리보기 → 적용). */
export function AutoMatchForm({ dateFrom, dateTo, corpId, onClose, onDone }: {
  dateFrom: string; dateTo: string; corpId: number | null; onClose: () => void; onDone: () => void;
}) {
  const [plan, setPlan] = useState<AutoMatchResult | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  useEffect(() => {
    api.autoMatchDeposits({ dateFrom, dateTo, corpId })
      .then(setPlan).catch((e) => setError(errMsg(e))).finally(() => setBusy(false));
  }, [dateFrom, dateTo, corpId]);

  async function commit() {
    setBusy(true); setError(null);
    try { const r = await api.autoMatchDeposits({ dateFrom, dateTo, corpId, commit: true }); setPlan(r); setDone(r.committed ?? 0); onDone(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="card space-y-3 p-5 text-sm">
      <h2 className="text-lg font-semibold">입금 자동 연결 — {dateFrom} ~ {dateTo}</h2>
      <p className="text-xs text-ink-soft">
        아직 연결 없는 원화 입금 중 <b>입금액 = 계산서 남은 금액(원 단위까지)</b>이고 <b>거래처 이름이 비슷한</b> 같은 법인 미입금 계산서가
        <b> 딱 한 장</b>인 것만 잇습니다. 짝이 여럿이거나 이름이 다르면 건너뜁니다 — 그런 입금은 줄의 「연결」에서 직접 고르세요.
      </p>
      {error && <ErrorBox message={error} />}
      {busy && !plan && <p className="text-ink-faint">찾는 중…</p>}
      {plan && (
        <>
          <p>{done !== null ? <b className="text-emerald-700">{done}건을 연결했습니다.</b> : <>입금 {plan.deposits}건 중 <b>{plan.pairs.length}건</b>을 이을 수 있습니다.</>}</p>
          {plan.pairs.length > 0 && (
            <div className="max-h-80 overflow-auto rounded border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white"><tr className="border-b text-left text-ink-soft">
                  <th className="px-2 py-1">입금일</th><th>법인</th><th>은행 내용</th><th className="text-right">금액</th><th>계산서</th><th>거래처</th><th>작성일자</th>
                </tr></thead>
                <tbody>
                  {plan.pairs.map((p) => (
                    <tr key={p.bankTxId} className="border-b">
                      <td className="whitespace-nowrap px-2 py-1">{p.txDate}</td><td>{p.corpName}</td><td>{dash(p.memo)}</td>
                      <td className="text-right">{money(p.amount)}</td><td className="whitespace-nowrap font-mono">{p.invoiceNo}</td><td>{p.custName}</td><td>{dash(p.writtenDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>{done !== null ? '닫기' : '취소'}</button>
        {done === null && <button className="btn-primary" disabled={busy || !plan || plan.pairs.length === 0} onClick={commit}>{plan?.pairs.length ?? 0}건 연결</button>}
      </div>
    </div>
  );
}
